import {
  Button,
  H2,
  InputEmail,
  InputField,
  InputCheckbox,
  InputText,
} from "@sellar-io/components";
import { ReactElement, useEffect, useState } from "react";
import TagManager from "react-gtm-module";
import { useForm, useStore } from "@tanstack/react-form";
import { useSearchParams } from "react-router";
import { useShallow } from "zustand/react/shallow";
import type { RetailerListElementResponseType } from "@sellar-io/api-v2/types";
import { SpinnerLegacy } from "~/components";
import { Alert } from "~/components/alert/Alert";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { BusinessAddressFormFragment } from "~/forms/business/BusinessAddressFormFragment";
import {
  fieldNote,
  getFieldError,
  requiredField,
} from "~/forms/business/formValidation";
import {
  generateBusinessFormFieldValues,
  generateBusinessRequest,
  generateSellarAddressFromGoogleResults,
  type BusinessFormFieldValues,
} from "~/forms/business/BusinessFormValues";
import { useSellarUser } from "~/hooks";
import { useBusinessClaim } from "~/hooks/api/v2/business/useBusinessClaim";
import { useBusinessCreate } from "~/hooks/api/v2/business/useBusinessCreate";
import {
  SignupFlowStep,
  useSignupContext,
} from "../../../../../hooks/useSignupContext.shared";
import { Link } from "react-router";
import { useBuyerCreationFlowStore } from "./BuyerCreationFlowStore";
import { useCreateTwoCompanyCreditLimitQuote } from "~/hooks/api/v2/two/useCreateTwoCompanyCreditLimitQuote";
import { BooleanSwitch } from "~/forms/business/BusinessFormFragment";

export function BuyerSignupFormStep({
  unAuthorisedPostcode,
}: {
  unAuthorisedPostcode?: string;
}): ReactElement {
  const creditLimitQuoteMutation = useCreateTwoCompanyCreditLimitQuote();
  const {
    setStep,
    signupDialogActive,
    endOnboarding,
    setRedirectToMarket,
    hasAcceptedTermsAndConditions,
    setCreditLimitQuoteValue,
    company,
    basketTotal,
    setBuyerSignUpBusinessId,
  } = useSignupContext();

  const { buyerDetails } = useBuyerCreationFlowStore(
    useShallow((state) => ({
      buyerDetails: state.buyerDetails,
    })),
  );
  const [searchParams] = useSearchParams();
  const claimBusinessMutation = useBusinessClaim();
  const businessCreateMutation = useBusinessCreate();
  const [loading, setLoading] = useState(false);
  const { changeBusiness } = useSellarUser();
  const [error, setError] = useState("");

  const storefrontLinkPassword =
    searchParams.get("storefrontLink") ?? searchParams.get("priceTierLink");
  const businessUserInviteJwt = searchParams.get("jwt");
  const utmMedium = searchParams.get("utm_medium");
  const handleFormSubmit = async (business: BusinessFormFieldValues) => {
    try {
      setError("");
      setLoading(true);
      const request = generateBusinessRequest(business, "buyer");
      if (request.type === "supplier") {
        throw new Error("Cannot create supplier");
      }
      // create buyer business
      const businessCreationResult = await businessCreateMutation.mutateAsync({
        data: request,
        sellarMetadata: {
          storefrontLinkPassword,
          businessUserInviteJwt,
          utmMedium,
          createdFrom: "Sign Up", // on buyer user sign up, simultaneously create buyer business
          initiator: "retailer",
        },
      });
      const businessId = businessCreationResult.id;
      // store created business Id
      setBuyerSignUpBusinessId(businessId);
      // claim business
      await claimBusinessMutation.mutateAsync({
        data: {
          id: businessId,
          hasAcceptedTermsAndConditions,
        },
        sellarMetadata: {
          storefrontLinkPassword,
          businessUserInviteJwt,
          utmMedium,
        },
      });

      // run credit check

      const creditCheckQuote =
        company?.companyNumber && businessCreationResult.Retailer?.id
          ? await creditLimitQuoteMutation.mutateAsync({
              twoOrganisationNumber: company.companyNumber,
              retailerId: businessCreationResult.Retailer.id,
            })
          : null;

      setCreditLimitQuoteValue(creditCheckQuote?.totalLimit ?? 0);

      // notify google tag manager of event
      TagManager.dataLayer({
        dataLayer: {
          event: "venue_claimed",
          businessId,
        },
      });

      // used to indicate if user has as draft basket and is signing up via a storefront page

      const hasBasketTotal = !!(basketTotal || basketTotal === 0);

      // if credit check value and no draft basket, navigate to CreditEligibleStep

      if (
        creditCheckQuote?.status === "eligible" &&
        creditCheckQuote.totalLimit > 0 &&
        !hasBasketTotal
      ) {
        return setStep(SignupFlowStep.CreditEligibleStep);
      }

      // if sign up dialog active, show complete onboarding step (handles draft basket)
      if (signupDialogActive) {
        // change to newly created business
        changeBusiness(businessId);
        return setStep(SignupFlowStep.ConfirmationStep);
      }
      // otherwise, not dialog flow and not credit eligible, complete sign up flow
      // change to newly created business
      changeBusiness(businessId);
      setRedirectToMarket(true);
      endOnboarding();
    } catch (error) {
      console.error(error);
      setError("An error has occurred. Please contact support.");
      setLoading(false);
    }
  };

  if (loading) return <SpinnerLegacy />;

  return (
    <>
      <H2 className={"mt-4 text-center"}>Your delivery address</H2>
      <p className={"text-center"}>
        If you have multiple sites, you can add them later
      </p>
      <div className="mt-4">
        <BuyerSignupEditForm
          onSubmit={handleFormSubmit}
          buyerDetails={buyerDetails}
          unAuthorisedPostcode={unAuthorisedPostcode}
        />
      </div>
      {error && <Alert type={"error"} message={error} />}
    </>
  );
}

const BuyerSignupEditForm = ({
  onSubmit,
  buyerDetails,
  unAuthorisedPostcode,
}: {
  onSubmit: (values: BusinessFormFieldValues) => void;
  buyerDetails?: RetailerListElementResponseType;
  unAuthorisedPostcode?: string;
}) => {
  const { searchGooglePlacesResult } = useBuyerCreationFlowStore(
    useShallow((state) => ({
      searchGooglePlacesResult: state.searchGooglePlacesResult,
    })),
  );

  const { hasAcceptedTermsAndConditions, setHasAcceptedTermsAndConditions } =
    useSignupContext();

  const { user } = useSellarUser();

  const [confirmedAssociation, setConfirmedAssociation] = useState(false);

  const form = useForm({
    defaultValues: searchGooglePlacesResult
      ? ({
          ...generateBusinessFormFieldValues(),
          name: searchGooglePlacesResult.name ?? "",
          addresses: {
            DeliveryAddress: generateSellarAddressFromGoogleResults(
              searchGooglePlacesResult,
            ),
            InvoiceAddress: generateSellarAddressFromGoogleResults(
              searchGooglePlacesResult,
            ),
            CollectionAddress: generateSellarAddressFromGoogleResults(
              searchGooglePlacesResult,
            ),
            isInvoiceAddressSame: true,
            isCollectionAddressSame: true,
          },
        } satisfies BusinessFormFieldValues)
      : generateBusinessFormFieldValues(),
    onSubmit: ({ value }) => {
      const submittedValues = {
        ...value,
        hasAcceptedTermsAndConditions,
      };

      onSubmit(submittedValues);
    },
  });

  useEffect(() => {
    if (user?.email && !form.state.values.email) {
      form.setFieldValue("email", user.email);
      form.setFieldValue("invoiceEmails", user.email);
    }

    if (
      unAuthorisedPostcode &&
      !form.state.values.addresses.DeliveryAddress.postalCode &&
      !buyerDetails?.Business.DeliveryAddress.postalCode
    ) {
      form.setFieldValue(
        "addresses.DeliveryAddress.postalCode",
        unAuthorisedPostcode,
      );
    }
  }, [form, user, unAuthorisedPostcode, buyerDetails]);

  const isInvoiceEmailAddressSame = useStore(
    form.store,
    (state) => state.values.isInvoiceEmailAddressSame,
  );

  return (
    <>
      <form className={"flex flex-col gap-4"} noValidate>
        <div className={"flex flex-col gap-2"}>
          <div>
            <InputLabel>Trading name</InputLabel>
            <InputContainer className={""}>
              <form.Field
                name="name"
                validators={requiredField("Trading name")}
              >
                {({ state, handleChange, handleBlur }) => {
                  const errorMessage = getFieldError(state.meta.errors);

                  return (
                    <div className="flex w-full flex-col gap-1">
                      <InputText
                        autoComplete={"organisation"}
                        name="name"
                        value={state.value}
                        onBlur={handleBlur}
                        onChange={(event) => handleChange(event.target.value)}
                        required
                      />
                      {fieldNote(undefined, errorMessage)}
                    </div>
                  );
                }}
              </form.Field>
            </InputContainer>
            <p className="mt-2 text-zinc-500">
              Use your venue or trade name if it differs from your registered
              business name.
            </p>
          </div>
          {!buyerDetails && (
            <>
              <InputLabel>Business email</InputLabel>
              <InputContainer>
                <InputEmail disabled={true} placeholder={user?.email} />
              </InputContainer>

              <>
                <div>
                  <div className="flex min-h-10 items-center gap-2 text-sm text-zinc-800">
                    <BooleanSwitch
                      form={form}
                      name="isInvoiceEmailAddressSame"
                      onChange={(checked) => {
                        if (user?.email && checked) {
                          form.setFieldValue("invoiceEmails", user.email);
                        }
                      }}
                    />
                    <div>My invoice email is the same as my business email</div>
                  </div>
                </div>
                {!isInvoiceEmailAddressSame && (
                  <InputField
                    label="Invoice email"
                    note={
                      "Set this field to indicate to the suppliers you order from where you'd like invoices sending to."
                    }
                  >
                    <form.Field
                      name="invoiceEmails"
                      validators={requiredField("Invoice email")}
                    >
                      {({ state, handleChange, handleBlur }) => {
                        const errorMessage = getFieldError(state.meta.errors);

                        return (
                          <>
                            <InputEmail
                              name="invoiceEmails"
                              value={state.value}
                              onBlur={handleBlur}
                              onChange={(event) =>
                                handleChange(event.target.value)
                              }
                              required
                            />
                            {fieldNote(undefined, errorMessage)}
                          </>
                        );
                      }}
                    </form.Field>
                  </InputField>
                )}
              </>
            </>
          )}
          <BusinessAddressFormFragment
            form={form}
            path={"addresses"}
            type="buyer"
          />
        </div>
        <BusinessClaimConfirmation
          onChange={(value) => setConfirmedAssociation(value)}
        />
        <TermsAndConditionsConfirmation
          onChange={(value) => setHasAcceptedTermsAndConditions(value)}
        />
      </form>
      <Button
        className={"mt-4 w-full"}
        disabled={!confirmedAssociation || !hasAcceptedTermsAndConditions}
        onClick={() => {
          void form.handleSubmit();
        }}
      >
        Continue
      </Button>
    </>
  );
};

export const BusinessClaimConfirmation = ({
  onChange,
}: {
  onChange: (value: boolean) => void;
}) => {
  return (
    <InputContainer className="@container w-full flex-none border-none bg-zinc-50 p-4">
      <InputCheckbox
        id="confirm-association"
        onChange={(e) => {
          onChange(e.target.checked);
        }}
        className="flex-none cursor-pointer"
      />
      <label
        htmlFor="confirm-association"
        className="min-w-0 flex-1 cursor-pointer"
      >
        I confirm I am associated with, and have permission to order on behalf
        of this business
      </label>
    </InputContainer>
  );
};

export const TermsAndConditionsConfirmation = ({
  onChange,
}: {
  onChange: (value: boolean) => void;
}) => {
  const { hasAcceptedTermsAndConditions } = useSignupContext();
  return (
    <InputContainer className="@container w-full flex-none border-none bg-zinc-50 p-4">
      <InputCheckbox
        id="tandc-confirmation"
        onChange={(e) => {
          onChange(e.target.checked);
        }}
        checked={hasAcceptedTermsAndConditions}
        className="flex-none cursor-pointer"
      />
      <label htmlFor="tandc-confirmation" className="min-w-0 flex-1">
        <p className={"gap-1"}>
          I agree to Sellar's
          <Link
            to={"https://www.sellar.io/terms"}
            target={"_blank"}
            className={"pl-1 text-center text-blue-600 underline"}
          >
            Terms and Conditions
          </Link>
        </p>
      </label>
    </InputContainer>
  );
};
